import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AlertCircle } from "lucide-react";
import FirebaseImageUpload from "@/components/firebase/FirebaseImageUpload";
import companyService from "@/services/companyService";

const EMPTY_FORM = {
  name: "",
  contactName: "",
  contactPhone: "",
  available: true,
  delivery: true,
  description: "",
  logoUrl: "",
  deliveryDescription: "",
  pickupDescription: "",
  eventsDescription: "",
  businessHoursWeekdays: "",
  businessHoursWeekend: "",
  footerInfo: "",
  pixKey: "",
  pixMerchantName: "",
  pixMerchantCity: "",
};

const FieldError = ({ message }) =>
  message ? (
    <div className="flex items-center gap-1 text-red-500 text-sm">
      <AlertCircle className="w-4 h-4" />
      <span>{message}</span>
    </div>
  ) : null;

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-gray-900 border-b pb-1">
    {children}
  </h3>
);

/**
 * Modal para criar ou editar empresa
 */
export default function CompanyFormModal({
  open,
  onOpenChange,
  company,
  onSave,
  loading,
}) {
  const isEditing = !!company;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  // Remonta o upload da logo quando o formulário é reiniciado
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (company) {
      const filled = { ...EMPTY_FORM };
      Object.keys(EMPTY_FORM).forEach((field) => {
        if (company[field] !== undefined && company[field] !== null) {
          filled[field] = company[field];
        }
      });
      setFormData(filled);
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
    setFormKey((key) => key + 1);
  }, [company, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = companyService.validateCompanyData(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSave(companyService.buildPayload(formData));
  };

  const textField = (field, label, props = {}) => (
    <div className="space-y-2">
      <Label htmlFor={`company-${field}`}>
        {label}
        {props.required && <span className="text-red-500"> *</span>}
      </Label>
      {props.hint && <p className="text-xs text-gray-500">{props.hint}</p>}
      <Input
        id={`company-${field}`}
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className={errors[field] ? "border-red-500" : ""}
        disabled={loading}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        onBlur={props.onBlur}
      />
      <FieldError message={errors[field]} />
    </div>
  );

  const textAreaField = (field, label, placeholder) => (
    <div className="space-y-2">
      <Label htmlFor={`company-${field}`}>{label}</Label>
      <Textarea
        id={`company-${field}`}
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className={errors[field] ? "border-red-500" : ""}
        disabled={loading}
        placeholder={placeholder}
        rows={2}
      />
      <FieldError message={errors[field]} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Empresa" : "Nova Empresa"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Código: ${company.code}`
              : "Preencha os dados da nova empresa. O código é gerado automaticamente."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados gerais */}
          <div className="space-y-4">
            <SectionTitle>Dados gerais</SectionTitle>
            {textField("name", "Nome da empresa", {
              required: true,
              placeholder: "Ex.: Bonfim Gourmet",
            })}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {textField("contactName", "Nome do contato", {
                placeholder: "Ex.: Maiara",
              })}
              {textField("contactPhone", "Telefone de contato", {
                placeholder: "Ex.: 11976881046",
                inputMode: "tel",
              })}
            </div>
            {textAreaField(
              "description",
              "Descrição",
              "Ex.: Geladinhos gourmet artesanais",
            )}
          </div>

          {/* Pagamento */}
          <div className="space-y-4">
            <SectionTitle>Pagamento</SectionTitle>
            <p className="text-xs text-gray-500">
              Dados do recebedor usados no QR code PIX dos pedidos desta
              empresa. Sem chave e cidade, o pagamento por PIX fica
              indisponível.
            </p>
            {textField("pixKey", "Chave PIX", {
              placeholder: "Ex.: contato@suaempresa.com.br",
              hint: "CPF/CNPJ, e-mail, celular com +55 (ex.: +5511976881046) ou chave aleatória. Deixe em branco para remover.",
            })}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {textField("pixMerchantName", "Nome do recebedor", {
                placeholder: "Ex.: Ulisses Mendes Bonfim",
                hint: "Até 120 caracteres. No QR code aparecem só os primeiros 25 (regra do Bacen). Em branco, usa o nome da empresa.",
              })}
              {textField("pixMerchantCity", "Cidade do recebedor", {
                placeholder: "Ex.: SAO JOSE DO RIO PRETO",
                hint: "Até 100 caracteres. No QR code aparecem só os primeiros 15 (regra do Bacen), sem acentos.",
              })}
            </div>
          </div>

          {/* Logo */}
          <div className="space-y-4">
            <SectionTitle>Logo</SectionTitle>
            <FirebaseImageUpload
              key={formKey}
              value={formData.logoUrl}
              onChange={(url) => handleChange("logoUrl", url)}
              folder="logo"
              companyCode={company?.code}
              placeholder="Clique ou arraste a logo da empresa"
            />
            {textField("logoUrl", "Ou informe a URL da logo", {
              placeholder: "https://...",
              // Atualiza a pré-visualização do upload com a URL digitada
              onBlur: () => setFormKey((key) => key + 1),
            })}
          </div>

          {/* Funcionamento */}
          <div className="space-y-4">
            <SectionTitle>Funcionamento</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {textField("businessHoursWeekdays", "Horário dias de semana", {
                placeholder: "Segunda a Sexta: 9h às 22h",
              })}
              {textField("businessHoursWeekend", "Horário fim de semana", {
                placeholder: "Sábado e Domingo: 10h às 20h",
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                <div>
                  <Label htmlFor="company-available">Loja aberta</Label>
                  <p className="text-xs text-gray-500">
                    Sistema ligado para receber pedidos
                  </p>
                </div>
                <Switch
                  id="company-available"
                  checked={formData.available}
                  onCheckedChange={(checked) =>
                    handleChange("available", checked)
                  }
                  disabled={loading}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-3">
                <div>
                  <Label htmlFor="company-delivery">Delivery</Label>
                  <p className="text-xs text-gray-500">
                    Aceita pedidos para entrega
                  </p>
                </div>
                <Switch
                  id="company-delivery"
                  checked={formData.delivery}
                  onCheckedChange={(checked) =>
                    handleChange("delivery", checked)
                  }
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Serviços */}
          <div className="space-y-4">
            <SectionTitle>Serviços</SectionTitle>
            {textAreaField(
              "deliveryDescription",
              "Descrição do delivery",
              "Ex.: Entregamos em toda a região",
            )}
            {textAreaField(
              "pickupDescription",
              "Descrição da retirada",
              "Ex.: Retire no local",
            )}
            {textAreaField(
              "eventsDescription",
              "Descrição de eventos",
              "Ex.: Atendemos eventos e festas",
            )}
            {textAreaField(
              "footerInfo",
              "Informações do rodapé",
              "Ex.: CNPJ, endereço, redes sociais",
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#1b5e20" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#144919")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#1b5e20")
              }
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
